import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Wand2, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { CollapsibleSection } from '@ooh/ui/components/collapsible-section';
import { importEndpoints } from '@ooh/contracts';
import type { AutofixRow, ImportErrorRow, ImportValidateResponse } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { apiQueryKey, useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { TableSkeleton } from '../../layouts/skeletons';
import { IssuesTable } from './validation-tables';
import { AutofixTable } from './autofix-table';
import { ValidationSummaryCards } from './validation-summary-cards';

interface ValidationStats {
  errorsOnly: ImportErrorRow[];
  autofixes: AutofixRow[];
  errorCount: number;
  skippedRowCount: number;
  validRowCount: number;
  allRowsHaveErrors: boolean;
}

const SUMMARY_TEST_IDS = {
  valid: 'summary-valid',
  errors: 'summary-errors',
  autofixes: 'summary-autofixes',
  skipped: 'summary-skipped',
} as const;

function hasAllRowsHaveErrors(validation: ImportValidateResponse | undefined): boolean {
  return (
    validation !== undefined &&
    validation.totalRows > 0 &&
    validation.errorRows === validation.totalRows
  );
}

export function computeValidationStats(
  validation: ImportValidateResponse | undefined,
): ValidationStats {
  const errorsOnly: ImportErrorRow[] = (validation?.errors ?? []).filter(
    (e) => e.severity === 'error',
  );
  const autofixes: AutofixRow[] = validation?.autofixes ?? [];
  const errorCount = errorsOnly.length;
  const skippedRowCount = validation?.errorRows ?? 0;
  const validRowCount = Math.max((validation?.totalRows ?? 0) - skippedRowCount, 0);
  const allRowsHaveErrors = hasAllRowsHaveErrors(validation);

  return { errorsOnly, autofixes, errorCount, skippedRowCount, validRowCount, allRowsHaveErrors };
}

interface AutofixSectionProps {
  autofixes: AutofixRow[];
}

function AutofixSection({ autofixes }: AutofixSectionProps) {
  const { t } = useTranslation('mediaImport');
  const [hideEmpty, setHideEmpty] = React.useState(true);

  const bannerText = t('validation.autofixCount', { count: autofixes.length });

  const trigger = (
    <>
      <Wand2 className="h-4 w-4 text-foreground shrink-0" />
      <span className="text-[13px] text-foreground" data-testid="autofix-banner">
        {bannerText}
      </span>
    </>
  );

  return (
    <CollapsibleSection trigger={trigger} testId="autofix-section" toggleTestId="autofix-toggle">
      <div data-testid="autofix-list">
        {autofixes.length > 0 ? (
          <AutofixTable
            autofixes={autofixes}
            hideEmpty={hideEmpty}
            onHideEmptyChange={setHideEmpty}
            hideEmptyLabel={t('validation.hideEmptyColumns')}
          />
        ) : (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">
            {t('validation.autofixEmpty')}
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}

interface ValidationPhaseProps {
  importJobId: number;
  onNext: () => void;
  /** Returns to the upload phase so the user can fix the file and re-upload (CU-86ca8125j). */
  onBack: () => void;
}

export function ValidationPhase(props: ValidationPhaseProps) {
  return (
    <QueryBoundary fallback={<TableSkeleton />}>
      <LoadedValidationPhase {...props} />
    </QueryBoundary>
  );
}

function LoadedValidationPhase({ importJobId, onNext, onBack }: ValidationPhaseProps) {
  const { t } = useTranslation('mediaImport');
  const api = useApiClient();
  const queryClient = useQueryClient();

  const { data: validation } = useApiSuspenseQuery(importEndpoints.validate, { id: importJobId });

  const startMutation = useMutation({
    mutationFn: () => api.call(importEndpoints.start, { id: importJobId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey(importEndpoints.getStatus, { id: importJobId }),
      });
      onNext();
    },
  });

  const { errorsOnly, autofixes, errorCount, skippedRowCount, validRowCount, allRowsHaveErrors } =
    computeValidationStats(validation);

  return (
    <div className="space-y-4" data-testid="phase-validation">
      <ValidationSummaryCards
        items={[
          {
            label: t('validation.summaryValid'),
            value: validRowCount,
            tone: 'success',
            testId: SUMMARY_TEST_IDS.valid,
          },
          {
            label: t('validation.summaryErrors'),
            value: errorCount,
            tone: 'destructive',
            testId: SUMMARY_TEST_IDS.errors,
          },
          {
            label: t('validation.summaryAutofixes'),
            value: autofixes.length,
            tone: 'warning',
            testId: SUMMARY_TEST_IDS.autofixes,
          },
          {
            label: t('validation.summarySkipped'),
            value: skippedRowCount,
            tone: 'neutral',
            testId: SUMMARY_TEST_IDS.skipped,
          },
        ]}
      />
      <AutofixSection autofixes={autofixes} />
      {errorCount > 0 && <IssuesTable errors={errorsOnly} />}
      {errorCount > 0 && (
        <p className="text-xs text-muted-foreground">{t('validation.warningText')}</p>
      )}
      {Boolean(allRowsHaveErrors) && (
        <p className="text-[13px] text-red-600 font-medium">{t('validation.allRowsHaveErrors')}</p>
      )}
      <div className="flex justify-between pt-1">
        <div className="flex gap-2">
          <Button variant="outline" type="button" onClick={onBack} data-testid="back-to-upload">
            <ChevronLeft className="h-4 w-4" />
            {t('validation.backToUpload')}
          </Button>
          <Button variant="outline" type="button" disabled data-testid="download-error-report">
            <Download className="h-4 w-4" />
            {t('validation.downloadErrorReport')}
          </Button>
        </div>
        <Button
          type="button"
          onClick={() => startMutation.mutate()}
          disabled={allRowsHaveErrors || startMutation.isPending}
          data-testid="next-to-progress"
        >
          {errorCount > 0 ? t('validation.importButtonSkip') : t('validation.importButton')}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
